import { RHFSelectInput } from "@impero-com/common/components/form/rhfFields/rhfSelectInput";
import { RHFStringInput } from "@impero-com/common/components/form/rhfFields/rhfStringInput";
import { assertNever } from "@impero-com/common/utils/obj";
import { Typography } from "antd";
import { Control } from "react-hook-form";
import { z } from "zod";

import type { SavedBlob } from "api/generated/model";
import { RHFCheckboxGroupInput } from "components/form/rhfFields/rhfCheckboxGroupInput";
import { RHFFileUploaderInput } from "components/form/rhfFields/rhfFileUploaderInput";
import { RHFMarkdownInput } from "components/form/rhfFields/rhfMarkdownInput";
import { RHFNumberInput } from "components/form/rhfFields/rhfNumberInput";
import { RHFRadioButtonGroupInput } from "components/form/rhfFields/rhfRadioButtonGroupInput";

import { HasTasks, InternalName, TasksField } from "./types";

const { Title } = Typography;
interface TaskFieldProps {
    field: TasksField;
    control: Control<HasTasks>;
    name: string;
    savedBlobs: SavedBlob[];
}
export function TaskField({
    field,
    control,
    name,
    savedBlobs,
}: TaskFieldProps) {
    const getFieldName: (id: string) => `${InternalName}.${string}` = (
        id: string
    ) => `${name as InternalName}.${id}`;
    switch (field.fieldType.type) {
        case "TextInput":
            return (
                <RHFStringInput
                    name={getFieldName(field.id)}
                    control={control}
                    schema={z.string()}
                />
            );
        case "Dropdown":
            const options = field.fieldType.options.map((option) => ({
                value: option,
                label: option,
            }));
            const optionsWithBlank = [{ value: null, label: "" }, ...options];
            return (
                <RHFSelectInput
                    name={getFieldName(field.id)}
                    options={optionsWithBlank}
                    control={control}
                    helpText={field.instructions}
                />
            );
        case "CheckboxGroup":
            return (
                <RHFCheckboxGroupInput
                    name={getFieldName(field.id)}
                    options={field.fieldType.options.map((option) => ({
                        value: option,
                        label: option,
                    }))}
                    control={control}
                />
            );

        case "Effectiveness": {
            const { fieldType } = field;
            const options = [
                {
                    value: "effective",
                    label: fieldType.effectiveOptionName,
                },
                {
                    value: "partiallyEffective",
                    label: fieldType.partiallyEffectiveOptionName,
                },
                {
                    value: "notEffective",
                    label: fieldType.notEffectiveOptionName,
                },
            ];
            return (
                <RHFRadioButtonGroupInput
                    name={getFieldName(field.id)}
                    options={options}
                    control={control}
                />
            );
        }
        case "TextArea":
            return (
                <RHFMarkdownInput
                    name={getFieldName(field.id)}
                    control={control}
                    schema={z.string()}
                />
            );
        case "NumericInput":
            return (
                <RHFNumberInput
                    name={getFieldName(field.id)}
                    control={control}
                    schema={z.number()}
                />
            );
        case "RadioButtonGroup":
            return (
                <RHFRadioButtonGroupInput
                    name={getFieldName(field.id)}
                    options={field.fieldType.options.map((option) => ({
                        value: option,
                        label: option,
                    }))}
                    control={control}
                    allowDeselect
                />
            );
        case "FileUpload":
            return (
                <RHFFileUploaderInput
                    name={getFieldName(field.id)}
                    control={control}
                    deleteAction={{
                        kind: "DELETE_BLOB",
                    }}
                    savedBlobs={savedBlobs}
                    hideMissing
                />
            );
        case "Heading":
            return <Title level={4}>{field.title}</Title>;
        default:
            return assertNever(field.fieldType);
    }
}
